import { screenToWorld, type Camera, type Point, type Rect } from '../camera';
import { snapBoxToGrid } from '../snapping';
import type { Intent, KeyInput, PointerInput, PointerType, Tool, ToolId } from './tool';

/** Movement under which a press is a click (screen px): a finger drifts more than a mouse (P5f review). */
export const CLICK_PX: Record<PointerType, number> = { mouse: 4, pen: 6, touch: 12 };

/**
 * A tool that creates an object by dragging its box (sections, frames, shapes): a click asks for the default size at
 * the point, a drag previews the box and creates exactly that box. Shift keeps the sides equal (square, circle), Alt
 * drags from the centre (P5f); a box is never under `minWorld` world units on a side. With a `grid`, both corners go
 * to the grid (P16) unless meta (Cmd/Ctrl) is held; the grid wins over an exact square.
 */
export function createBoxTool(kind: ToolId, camera: () => Camera, opts: { minWorld?: number; grid?: () => number | null } = {}): Tool {
  const minWorld = opts.minWorld ?? 1;
  let start: { screen: Point; world: Point; type: PointerType } | null = null;
  const onGrid = (r: Rect, e: PointerInput): Rect => {
    const g = e.meta ? null : opts.grid?.();
    if (!g) return r;
    const b = snapBoxToGrid({ x: r.minX, y: r.minY, w: r.maxX - r.minX, h: r.maxY - r.minY }, g);
    return { minX: b.x, minY: b.y, maxX: b.x + b.w, maxY: b.y + b.h };
  };
  const freeRect = (e: PointerInput): Rect => {
    const p = screenToWorld(camera(), e);
    const s = start!.world;
    let dx = p.x - s.x;
    let dy = p.y - s.y;
    if (e.shift) {
      const side = Math.max(Math.abs(dx), Math.abs(dy));
      dx = (dx < 0 ? -1 : 1) * side;
      dy = (dy < 0 ? -1 : 1) * side;
    }
    // Alt: the press is the centre, the box spans the drag on both sides.
    const half = e.alt ? minWorld / 2 : minWorld;
    dx = (dx < 0 ? -1 : 1) * Math.max(Math.abs(dx), half);
    dy = (dy < 0 ? -1 : 1) * Math.max(Math.abs(dy), half);
    if (e.alt) return { minX: s.x - Math.abs(dx), minY: s.y - Math.abs(dy), maxX: s.x + Math.abs(dx), maxY: s.y + Math.abs(dy) };
    return { minX: Math.min(s.x, s.x + dx), minY: Math.min(s.y, s.y + dy), maxX: Math.max(s.x, s.x + dx), maxY: Math.max(s.y, s.y + dy) };
  };
  const rectTo = (e: PointerInput): Rect => onGrid(freeRect(e), e);
  const dragged = (e: PointerInput) => Math.hypot(e.x - start!.screen.x, e.y - start!.screen.y) >= CLICK_PX[start!.type];
  const clear: Intent = { kind: 'boxPreview', tool: kind, rect: null };

  return {
    onPointerDown(e) {
      start = { screen: { x: e.x, y: e.y }, world: screenToWorld(camera(), e), type: e.pointerType };
      return [];
    },
    onPointerMove(e) {
      if (!start || !dragged(e)) return [];
      return [{ kind: 'boxPreview', tool: kind, rect: rectTo(e) }];
    },
    onPointerUp(e) {
      if (!start) return [];
      const at = start.world;
      if (!dragged(e)) {
        start = null;
        return [{ kind: 'create', tool: kind, at }];
      }
      const r = rectTo(e);
      start = null;
      const box = { x: r.minX, y: r.minY, w: r.maxX - r.minX, h: r.maxY - r.minY };
      return [clear, { kind: 'createBox', tool: kind, box }] satisfies Intent[];
    },
    onKey(e: KeyInput) {
      if (e.key !== 'Escape' || !start) return [];
      start = null;
      return [clear, { kind: 'cancel' }];
    },
    onCancel() {
      const was = start !== null;
      start = null;
      return was ? [clear, { kind: 'cancel' }] : [];
    },
  };
}
